import type { User } from "firebase/auth";
import { SYNC_QUEUE_CHANGED_EVENT, listPendingOfflineChanges, markOfflineChangeError, removeOfflineChange } from "./localDb";
import { attachInspectionPhoto, uploadInspectionPhotos, type OfflineInspectionPhotoPayload } from "./inspections";
import { attachDamagePhoto, type OfflineDamagePhotoPayload } from "./damages";

export { SYNC_QUEUE_CHANGED_EVENT };

export function isOnline(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

export function subscribeOnlineStatus(onChange: (online: boolean) => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleOnline = () => onChange(true);
  const handleOffline = () => onChange(false);
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
}

export async function countPendingSyncPhotos(): Promise<number> {
  const pending = await listPendingOfflineChanges();
  return pending.filter((change) => change.entity === "inspectionPhoto" || change.entity === "damagePhoto").length;
}

let isSyncing = false;

export async function processPendingSync(user: User | null): Promise<{ processed: number; failed: number }> {
  if (!user || !isOnline() || isSyncing) {
    return { processed: 0, failed: 0 };
  }

  isSyncing = true;
  let processed = 0;
  let failed = 0;

  try {
    const pending = await listPendingOfflineChanges();

    for (const change of pending) {
      if (change.entity !== "inspectionPhoto" && change.entity !== "damagePhoto") {
        continue;
      }

      try {
        if (change.entity === "inspectionPhoto") {
          const payload = change.payload as OfflineInspectionPhotoPayload;
          const [url] = await uploadInspectionPhotos(payload.companyId, payload.assetId, [payload.file]);
          if (!url) {
            throw new Error("Falha ao enviar foto da inspeção.");
          }

          await attachInspectionPhoto(payload.companyId, payload.assetId, payload.inspectionId, payload.questionId, url, user);
        } else {
          const payload = change.payload as OfflineDamagePhotoPayload;
          const [url] = await uploadInspectionPhotos(payload.companyId, payload.assetId, [payload.file]);
          if (!url) {
            throw new Error("Falha ao enviar foto da avaria.");
          }

          await attachDamagePhoto(payload.companyId, payload.assetId, payload.damageId, url, user);
        }

        await removeOfflineChange(change.id);
        processed += 1;
      } catch (error) {
        failed += 1;
        await markOfflineChangeError(change.id, error instanceof Error ? error.message : "Falha ao sincronizar foto.");
      }
    }
  } finally {
    isSyncing = false;
  }

  return { processed, failed };
}
